import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Card, Empty, Spin } from 'antd';
import { useMemo, useState } from 'react';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { invalidateOperationAnalysis } from '@/common/utils/operation-analysis-scope';
import OperationEntityForm from '../components/OperationEntityForm';
import OperationEntityPrincipalManagement from '../components/OperationEntityPrincipalManagement';
import {
  batchUpdateOperationEntities,
  createOperationEntity,
  deleteOperationEntity,
  queryOperationEntities,
  updateOperationEntity,
} from '../services/operations';
import { queryMembers } from '../services/members';

const getMemberOptions = (members) =>
  members.map((member) => ({
    label:
      member.user?.name ||
      member.user?.phoneNumber ||
      member.name ||
      member.phoneNumber ||
      member.id,
    value: member.id,
  }));

const OperationEntityManagement = () => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationPath();
  const [editingEntity, setEditingEntity] = useState(null);
  const [principalContext, setPrincipalContext] = useState(null);
  const [creating, setCreating] = useState(false);
  const { data: entities = [], isPending: isEntitiesPending } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryOperationEntities({ organizationId }),
    queryKey: ['settings-operation-entities', organizationId],
  });
  const { data: memberResult = { data: [] } } = useQuery({
    enabled: Boolean(organizationId),
    queryFn: () => queryMembers({ current: 1, organizationId, pageSize: 100 }),
    queryKey: ['settings-operation-entity-members', organizationId],
  });
  const memberOptions = useMemo(
    () => getMemberOptions(memberResult.data || []),
    [memberResult.data],
  );
  const invalidateEntities = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['settings-operation-entities', organizationId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['operationEntityPrincipalMatches', organizationId],
      }),
      queryClient.invalidateQueries({
        queryKey: ['settings-operation-entity-members', organizationId],
      }),
      invalidateOperationAnalysis(queryClient, organizationId),
    ]);
  const { mutateAsync: createEntity } = useMutation({ mutationFn: createOperationEntity });
  const { mutateAsync: updateEntity } = useMutation({ mutationFn: updateOperationEntity });
  const { mutateAsync: deleteEntity } = useMutation({ mutationFn: deleteOperationEntity });
  const { mutateAsync: batchUpdateEntities } = useMutation({
    mutationFn: batchUpdateOperationEntities,
  });
  const open = creating || Boolean(editingEntity);

  const closeForm = () => {
    setCreating(false);
    setEditingEntity(null);
    setPrincipalContext(null);
  };

  const removeEntity = async (entity) => {
    try {
      await deleteEntity({ data: { id: entity.id, organizationId } });
      message.success('区域实体已删除');
      await invalidateEntities();
      return true;
    } catch (error) {
      message.error(error.message);
      return false;
    }
  };

  const saveEntity = async (values) => {
    try {
      await (editingEntity ? updateEntity : createEntity)({
        data: { ...values, organizationId },
      });
      message.success(editingEntity ? '经营实体已更新' : '经营实体已创建');
      await invalidateEntities();
      closeForm();
      return true;
    } catch (error) {
      message.error(error.message);
      return false;
    }
  };

  const saveBatch = async (values) => {
    try {
      await batchUpdateEntities({ data: { ...values, organizationId } });
      message.success(values.action === 'move' ? '门店上级已批量调整' : '门店状态已批量更新');
      await invalidateEntities();
      return true;
    } catch (error) {
      message.error(error.message);
      return false;
    }
  };

  return (
    <>
      {isEntitiesPending || !entities.length ? (
        <Card title="组织结构" variant="borderless">
          {isEntitiesPending ? <Spin /> : <Empty description="暂无经营实体" />}
        </Card>
      ) : (
        <OperationEntityPrincipalManagement
          entities={entities}
          onBatchUpdate={saveBatch}
          onCreate={() => setCreating(true)}
          onDelete={removeEntity}
          onEdit={(entity, context) => {
            setEditingEntity(entity);
            setPrincipalContext(context);
          }}
          organizationId={organizationId}
        />
      )}
      {open ? (
        <OperationEntityForm
          entities={entities}
          entity={editingEntity}
          memberOptions={memberOptions}
          principalContext={principalContext}
          open={open}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              closeForm();
            }
          }}
          onFinish={saveEntity}
        />
      ) : null}
    </>
  );
};

export default OperationEntityManagement;
